import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProductCard from "./ProductCard";
import { useCatalog, useCategories } from "../hooks/useCatalog";
import { ChevronDown, ChevronUp, Loader2, Search } from "lucide-react";
import { PRODUCTS as P_TID } from "../constants/testIds";
import { Link } from "react-router-dom";
import { CATEGORY_SEO } from "../data/seoPages";

const VISIBLE_INITIAL = 18;

const Products = () => {
    const [showAll, setShowAll] = useState(false);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const { data: catalogData, isLoading: catalogLoading, error: catalogError } = useCatalog();
    const { data: categoriesData, isLoading: categoriesLoading } = useCategories();

    const products = useMemo(() => catalogData?.items || [], [catalogData]);
    const categories = useMemo(() => categoriesData || [], [categoriesData]);

    const filtered = useMemo(() => {
        let result = products;
        if (filter !== "all") {
            const cat = categories.find((c) => c.id === filter);
            result = result.filter((p) => p.category === cat?.label);
        }
        const q = search.trim().toLowerCase();
        if (q) {
            result = result.filter((p) => {
                const nameMatch = p.name?.toLowerCase().includes(q);
                const specMatch = (p.specs || []).some((s) =>
                    s.toLowerCase().includes(q),
                );
                return nameMatch || specMatch;
            });
        }
        return result;
    }, [filter, products, categories, search]);

    const visible = showAll ? filtered : filtered.slice(0, VISIBLE_INITIAL);
    const hasMore = filtered.length > VISIBLE_INITIAL;

    const isLoading = catalogLoading || categoriesLoading;

    return (
        <section
            id="produits"
            data-testid={P_TID.section}
            className="relative bg-[#FBFBFD] pt-14 pb-24 md:pt-16 md:pb-32"
        >
            <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <div className="dp-eyebrow">Catalogue</div>
                        <h2 className="mt-3 font-display text-4xl md:text-5xl lg:text-[56px] font-light tracking-tight text-neutral-900 leading-[1.05]">
                            Tous nos instruments,{" "}
                            <span className="italic text-neutral-500">prêts à expédier.</span>
                        </h2>
                    </div>
                    <p className="text-[15px] text-neutral-500 max-w-md">
                        Sélectionnez vos quantités, ajoutez-les au panier, puis envoyez votre
                        demande de devis directement sur WhatsApp.
                    </p>
                </div>

                {/* Category filter */}
                <div className="mt-10 flex flex-wrap items-center gap-2">
                    <div className="relative flex items-center">
                        <Search className="absolute left-3.5 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
                        <input
                            id="catalog-search"
                            type="text"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setShowAll(false);
                            }}
                            placeholder="Rechercher…"
                            className="inline-flex items-center font-medium text-[12.5px] py-2 pl-10 pr-4 min-w-[170px] md:min-w-[220px] rounded-full border border-black/[0.12] bg-transparent text-neutral-900 placeholder:text-neutral-500 cursor-text focus:outline-none focus:ring-2 focus:ring-[#0a84ff]/20 focus:border-[#0a84ff]/40"
                        />
                    </div>
                    <button
                        type="button"
                        data-testid="filter-all"
                        onClick={() => {
                            setFilter("all");
                            setShowAll(false);
                        }}
                        className={`dp-btn text-[12.5px] py-2 px-4 ${
                            filter === "all" ? "dp-btn-primary" : "dp-btn-ghost"
                        }`}
                    >
                        Tous · {products.length}
                    </button>
                    {categories.map((c) => {
                        const count = products.filter((p) => p.category === c.label).length;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`filter-${c.id}`}
                                onClick={() => {
                                    setFilter(c.id);
                                    setShowAll(false);
                                }}
                                className={`dp-btn text-[12.5px] py-2 px-4 ${
                                    filter === c.id ? "dp-btn-primary" : "dp-btn-ghost"
                                }`}
                            >
                                {c.label} · {count}
                            </button>
                        );
                    })}
                </div>

                {/* SEO category pages */}
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
                    {categories.map((c) => {
                        const seo = CATEGORY_SEO[c.id];
                        return seo ? (
                            <Link
                                key={c.id}
                                to={seo.path}
                                className="text-[#0a84ff] hover:underline font-medium"
                            >
                                Voir la page {c.label}
                            </Link>
                        ) : null;
                    })}
                </div>

                {/* Loading state */}
                {isLoading && (
                    <div className="mt-16 flex flex-col items-center justify-center gap-4 text-neutral-500">
                        <Loader2 className="w-8 h-8 animate-spin text-[#0a84ff]" />
                        <span className="text-sm font-medium">Chargement du catalogue…</span>
                    </div>
                )}

                {/* Error state */}
                {catalogError && !isLoading && (
                    <div className="mt-10 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                        <p className="font-medium">Impossible de charger le catalogue.</p>
                        <p className="text-sm mt-1">{catalogError.message}</p>
                    </div>
                )}

                {/* Grid */}
                {!isLoading && (
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        <AnimatePresence mode="popLayout">
                            {visible.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && visible.length === 0 && (
                    <div className="mt-16 text-center text-neutral-500">
                        Aucun produit ne correspond à {search.trim() ? "cette recherche" : "cette catégorie"}.
                    </div>
                )}

                {/* Voir tout */}
                {hasMore && !isLoading && (
                    <div className="mt-12 flex flex-col items-center gap-3">
                        <button
                            type="button"
                            data-testid="products-see-all"
                            onClick={() => setShowAll((v) => !v)}
                            className="dp-btn dp-btn-ghost"
                        >
                            {showAll ? (
                                <>
                                    <ChevronUp className="w-4 h-4" />
                                    Réduire
                                </>
                            ) : (
                                <>
                                    Voir tout ({filtered.length})
                                    <ChevronDown className="w-4 h-4" />
                                </>
                            )}
                        </button>
                        <div className="text-[12px] font-mono-tech tracking-[0.2em] uppercase text-neutral-400">
                            {visible.length} / {filtered.length} produits affichés
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Products;
